import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, EMPTY, catchError, finalize, first, tap } from 'rxjs';
import { InventoryItemGeneralInfoUpdate } from 'src/app/core/models/inventory/inventory-item-general-info-update.interface';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { InventoryItemOverviewFormModel } from 'src/app/core/models/inventory/inventory-item-overview-form.model';
import {
  InventoryItemOverviewEditableFieldName,
  InventoryItemOverviewField,
} from 'src/app/core/models/inventory/inventory-item-overview-view-model.interface';
import { CoreModule } from 'src/app/core/core.module';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { isConcurrencyConflictError } from 'src/app/core/utils/api-error.util';

type InventoryItemIdentificationFieldName = Extract<
  InventoryItemOverviewEditableFieldName,
  'number' | 'gtin' | 'id' | 'source' | 'lastSyncDate' | 'updatedAt'
>;

type InventoryItemIdentificationEditableFieldName = Extract<
  InventoryItemIdentificationFieldName,
  'number' | 'gtin'
>;

type InventoryItemIdentificationPreviewUpdater = (
  item: InventoryItem,
  value: string,
) => Omit<InventoryItemGeneralInfoUpdate, 'expectedConcurrencyToken'>;

interface InventoryItemIdentificationViewModel {
  fields: Array<
    InventoryItemOverviewField & {
      controlName: InventoryItemIdentificationFieldName;
      editableFieldName?: InventoryItemIdentificationEditableFieldName;
    }
  >;
  previewFieldLabelKey?: string;
}

@Component({
  selector: 'app-inventory-item-identification-tab',
  templateUrl: './inventory-item-identification-tab.component.html',
  styleUrl: './inventory-item-identification-tab.component.scss',
  imports: [CommonModule, ReactiveFormsModule, CoreModule],
  providers: [InventoryItemOverviewFormModel],
  standalone: true,
})
export class InventoryItemIdentificationTabComponent implements OnChanges {
  readonly overviewForm = inject(InventoryItemOverviewFormModel);
  private readonly inventoryItemService = inject(InventoryItemService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public itemId = '';
  @Input() public inventoryItem: InventoryItem | null = null;
  @Output() public readonly inventoryItemChange = new EventEmitter<InventoryItem>();
  @Output() public readonly concurrencyConflict = new EventEmitter<void>();

  public isLoading = false;
  public readonly inventoryItem$ = new BehaviorSubject<InventoryItem | null>(null);
  public readonly viewModel$ = new BehaviorSubject<InventoryItemIdentificationViewModel | null>(null);

  private readonly patchUpdaters: Record<
    InventoryItemIdentificationEditableFieldName,
    InventoryItemIdentificationPreviewUpdater
  > = {
    number: (_, value) => ({ number: this.toOptionalString(value) }),
    gtin: (_, value) => ({ gtin: this.toOptionalString(value) }),
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['companyId'] && !changes['itemId'] && !changes['inventoryItem']) {
      return;
    }

    if (this.inventoryItem) {
      this.syncInventoryItemState(this.inventoryItem);
      return;
    }

    this.loadInventoryItem();
  }

  public onPreviewFieldUpdate(fieldName: InventoryItemIdentificationEditableFieldName): void {
    const currentItem = this.inventoryItem$.getValue();
    if (!currentItem) {
      return;
    }

    const rawValue = this.overviewForm.form.controls[fieldName].value ?? '';
    const changes = this.withConcurrencyMetadata(
      currentItem,
      this.patchUpdaters[fieldName](currentItem, rawValue.trim()),
    );

    this.inventoryItemService
      .updateInventoryItemGeneralInfo(this.companyId, this.itemId, changes)
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem, this.getFieldLabelKey(fieldName));
          this.inventoryItemChange.emit(inventoryItem);
        }),
        catchError((error) => {
          this.syncInventoryItemState(currentItem);
          if (isConcurrencyConflictError(error)) {
            this.concurrencyConflict.emit();
          }

          return EMPTY;
        }),
      )
      .subscribe();
  }

  private loadInventoryItem(): void {
    if (!this.companyId || !this.itemId) {
      return;
    }

    this.isLoading = true;
    this.inventoryItemService
      .getInventoryItem(this.companyId, this.itemId)
      .pipe(
        first(),
        tap((inventoryItem) => this.syncInventoryItemState(inventoryItem)),
        catchError(() => EMPTY),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private syncInventoryItemState(inventoryItem: InventoryItem, previewFieldLabelKey?: string): void {
    this.inventoryItem$.next(inventoryItem);
    this.overviewForm.form.patchValue(
      {
        number: inventoryItem.number ?? '',
        gtin: inventoryItem.gtin ?? '',
        id: inventoryItem.id,
        source: this.resolveSourceValueKey(inventoryItem),
        lastSyncDate: inventoryItem.lastSyncDate ?? '',
        updatedAt: inventoryItem.lastModifiedDateTime ?? '',
      },
      { emitEvent: false },
    );
    this.viewModel$.next(this.buildViewModel(inventoryItem, previewFieldLabelKey));
  }

  private buildViewModel(
    item: InventoryItem,
    previewFieldLabelKey?: string,
  ): InventoryItemIdentificationViewModel {
    const isEditable = item.isArchived !== true;

    return {
      previewFieldLabelKey,
      fields: [
        {
          controlName: 'number',
          editableFieldName: 'number',
          label: 'COMPONENTS.GOODS.FIELDS.SKU',
          value: item.number ?? '',
          valueClass: 'inventory-item-identification-tab__field-value--mono',
          editable: isEditable,
        },
        {
          controlName: 'gtin',
          editableFieldName: 'gtin',
          label: 'COMPONENTS.GOODS.FIELDS.GTIN',
          value: item.gtin ?? '',
          valueClass: 'inventory-item-identification-tab__field-value--mono',
          editable: isEditable,
        },
        {
          controlName: 'id',
          label: 'COMPONENTS.GOODS.FIELDS.ID',
          value: item.id,
          valueClass: 'inventory-item-identification-tab__field-value--mono',
          editable: false,
        },
        {
          controlName: 'source',
          label: 'COMPONENTS.GOODS.FIELDS.SOURCE',
          value: this.resolveSourceValueKey(item),
          valueKey: this.resolveSourceValueKey(item),
          valueClass: 'inventory-item-identification-tab__field-value--source',
          editable: false,
        },
        {
          controlName: 'lastSyncDate',
          label: 'COMPONENTS.GOODS.FIELDS.LAST_SYNC_DATE',
          value: item.lastSyncDate ?? '',
          editable: false,
        },
        {
          controlName: 'updatedAt',
          label: 'COMPONENTS.GOODS.FIELDS.UPDATED_AT',
          value: item.lastModifiedDateTime ?? '',
          editable: false,
        },
      ],
    };
  }

  private getFieldLabelKey(fieldName: InventoryItemIdentificationEditableFieldName): string {
    const labels: Record<InventoryItemIdentificationEditableFieldName, string> = {
      number: 'COMPONENTS.GOODS.FIELDS.SKU',
      gtin: 'COMPONENTS.GOODS.FIELDS.GTIN',
    };

    return labels[fieldName];
  }

  private resolveSourceValueKey(item: InventoryItem): string {
    return item.businessCentralId
      ? 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES.BUSINESS_CENTRAL'
      : 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES.ORGLINES';
  }

  private toOptionalString(value: string): string | undefined {
    return value ? value : undefined;
  }

  private withConcurrencyMetadata<T extends object>(
    inventoryItem: InventoryItem,
    changes: T,
  ): T & { expectedConcurrencyToken: string } {
    return {
      ...changes,
      expectedConcurrencyToken: inventoryItem.concurrencyToken ?? '',
    };
  }
}
